import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { User } from '../../models/user';
import { CustomerService } from '../../services/customer.service';
import { ErrorService } from '../../services/error.service';
import { FileService } from '../../services/file.service';
import { SnackbarService } from '../../services/snackbar.service';
import { UserService } from '../../services/user.service';
import { ModalComponent } from '../modal/modal.component';

@Component({
	selector: 'app-attachments-drag-drop',
	templateUrl: './attachments-drag-drop.component.html',
	styleUrls: ['./attachments-drag-drop.component.scss']
})
export class AttachmentsDragDropComponent implements OnInit {
	@Input() public access: number;
	@Input() public dialog: any;
	public disallowedExtensions: string[];
	public fileExtensionRegex = /(?:\.([^.]+))?$/;
	@ViewChild('fileDropRef') public fileInput: ElementRef;
	public podAttachments: any[] = [];
	public progressInterval: any;
	@Output() public refreshData: EventEmitter<number> = new EventEmitter<number>();
	public regularAttachments: any[] = [];
	public user: User;
	@Input() public workOrder: any;

	constructor(private _dialog: MatDialog,
				private _fileService: FileService,
				private _errorService: ErrorService,
				private _userService: UserService,
				private _snackbarService: SnackbarService,
				private _customerService: CustomerService) {
	}

	encodeFile(file: File): void {
		const self = this;
		const myReader: FileReader = new FileReader();
		this.uploadFilesSimulator(file);
		myReader.onloadend = e => {
			self.sendFile(myReader.result, file.name);
		};

		myReader.readAsDataURL(file);
	}

	fileBrowseHandler(files): void {
		this.prepareFilesList(files);
	}

	ngOnInit(): void {
		this._userService.user$.subscribe((user) => {
			this.user = user;
		});
		this.disallowedExtensions = this._fileService.getDisallowedExtensions();
		this.processAttachments();
	}

	onFileDropped($event): void {
		this.prepareFilesList($event);
	}

	openFile(file): void {
		const fileType = this.fileExtensionRegex.exec(file.fileName)[1];
		const selectedCustomer = this._customerService.getSelectedCustomer();
		const convertTo = fileType === 'pdf' ? fileType : selectedCustomer ? selectedCustomer.defaultImageConversion : 'NO_CONVERSION';
		this._fileService.getFile(file.id, convertTo).subscribe(
			(response) => {
				// Success
				// We get base64 but need a blob so convert it, using mime-types library to
				// guess the mime type based on file extension - This is not fool proof as file
				// extensions can be spoofed
				const blob = this._fileService.convertToBlob(response);
				// If IE or Edge
				if ((window.navigator as any).msSaveBlob) {
					return (window.navigator as any).msSaveBlob(blob, response.fileName);
				}
				const reader: any = new FileReader();
				reader.onload = (e) => {
					let content;
					if (reader.result.includes('image')) {
						content = new Image();
						content.src = reader.result;
					}
					const newTab = window.open();
					if (newTab) {
						newTab.document.write(content ? content.outerHTML : '<iframe src="' + reader.result + '" frameborder="0" class="no-border-full-screen" allowfullscreen></iframe>');
					} else {
						this._errorService.error({
							detailedMsg: 'Pop-up Blocker is enabled! Please add this site to your exception list.',
							generalMsg: 'Pop-up blocked!'
						}, null, 'modal');
					}
				};
				reader.readAsDataURL(blob);
			},
			(error) => {
				this._errorService.error(error, 'openFile Failed', 'modal');
			}
		);
	}

	prepareFilesList(files: Array<any>): void {
		const tooLarge = [];
		const badExtensions = [];
		const snackbarMessages = [];
		for (const file of files) {
			file.progress = 0;
			file.fileName = file.name;
			const filesize = Math.round(((file.size / 1024) / 1024)); // MB
			if (filesize > this.user.maxAttachmentSize) {
				tooLarge.push(file.name);
				continue;
			}
			const ext = file.name.substr(file.name.lastIndexOf('.'));
			if (this.disallowedExtensions.indexOf(ext.toUpperCase()) > -1) {
				badExtensions.push(ext);
				continue;
			}
			this.regularAttachments = [...this.regularAttachments, file];
			this.encodeFile(file);
		}
		this.fileInput.nativeElement.value = null;
		if (tooLarge.length > 0) {
			snackbarMessages.push(`The following files were larger than the allowed ${this.user.maxAttachmentSize} file size and cannot be uploaded: ${tooLarge.join(', ')}`);
		}
		if (badExtensions.length > 0) {
			snackbarMessages.push(`The following file extensions were not allowed to be uploaded: ${badExtensions.join(', ')}`);
		}
		for (const message of snackbarMessages) {
			this._snackbarService.openSnackBar(message, 'Close', 5000);
		}
	}

	processAttachments(): void {
		if (this.workOrder?.attachments?.length > 0) {
			const attachments = this.workOrder.attachments.map((attachment) => {
				attachment.progress = 100;
				return attachment;
			});
			this.regularAttachments = attachments.filter((a) => a.fileCategory !== 'POD_IMAGE');
			this.podAttachments = attachments.filter((a) => a.fileCategory === 'POD_IMAGE');
		}
	}

	removeAttachment(file): void {
		const dialogRef = this._dialog.open(ModalComponent, {
			data: {
				title: 'Warning',
				message: 'You are about to delete an attachment.  Would you like to proceed?',
				showCancel: true,
				cancelText: 'No',
				okText: 'Yes'
			}
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this._fileService.deleteFile(file.id).subscribe(() => {
					const idx = this.workOrder.attachments.indexOf(file);
					if (idx !== -1) {
						this.workOrder.attachments.splice(idx, 1);
						this.processAttachments();
						this.refreshData.emit(this.workOrder.id || this.workOrder.stopId);
					}
				}, error => {
					this._errorService.error(error, 'deleteFile Failed', 'modal');
				});
			} else {
				return false;
			}
		});
	}

	sendFile(file, fileName): void {
		const fileData = {
			parentDocType: this.workOrder.type === 'ROUTE_STOP' || this.workOrder.type === 'ROUTESTOP' ? 'ROUTE_STOP' : 'ORDER',
			parentDocId: this.workOrder.id || this.workOrder.stopId,
			fileName: fileName,
			description: fileName,
			base64EncodedData: file.split(',')[1]
		};
		this._fileService.uploadFile(fileData).subscribe((response) => {
			this.workOrder.attachments.push({fileName: fileName, id: response, progress: 100});
			clearInterval(this.progressInterval);
			this.processAttachments();
			this.refreshData.emit(fileData.parentDocId);
		}, (error) => {
			this.processAttachments();
			this._errorService.error(error, 'uploadFile Failed', 'modal');
		});
	}

	uploadFilesSimulator(file): void {
		this.progressInterval = setInterval(() => {
			if (file.progress === 100) {
				clearInterval(this.progressInterval);
			} else if (file.progress < 90) {
				file.progress += 5;
			} else if (file.progress === 90) {
				file.progress -= 1;
			}
		}, 200);
	}
}
